<template>
  <div class="help-feedback" :class="{ 'help-feedback--submitted': hasSubmitted }">
    <p class="help-feedback__question">是否对您有帮助</p>
    <div class="help-feedback__buttons">
      <RoundOutlineButton
        type="default"
        size="small"
        :active="feedbackType === 'yes'"
        :disabled="lockedType && lockedType !== 'yes'"
        :class="{ 'help-feedback__btn--dimmed': lockedType && lockedType !== 'yes' }"
        @click="handleClick('yes')"
      >
        <template #icon>
          <icon-thumb-up />
        </template>
        是
      </RoundOutlineButton>
      <RoundOutlineButton
        type="default"
        size="small"
        :active="feedbackType === 'no'"
        :disabled="lockedType && lockedType !== 'no'"
        :class="{ 'help-feedback__btn--dimmed': lockedType && lockedType !== 'no' }"
        @click="handleClick('no')"
      >
        <template #icon>
          <icon-thumb-down />
        </template>
        否
      </RoundOutlineButton>
    </div>
    <div v-if="hasSubmitted" class="help-feedback__result">
      <IconFaceSmileFill
        v-if="feedbackType === 'yes'"
        class="help-feedback__emoji help-feedback__emoji--colored"
      />
      <IconFaceMehFill v-else class="help-feedback__emoji" />
      <span class="help-feedback__result-text">{{
        feedbackType === 'yes' ? yesMessage : noMessage
      }}</span>
    </div>
  </div>
</template>

<script setup>
/**
 * @description 帮助反馈组件
 * @emits feedback - 反馈事件，传递反馈类型 'yes' 或 'no'
 */

import { ref, onMounted, watch, computed } from 'vue'
import { BaseMessage } from '~/utils/baseMessage'
import {
  IconThumbUp,
  IconThumbDown,
  IconFaceSmileFill,
  IconFaceMehFill,
} from '@arco-design/web-vue/es/icon'
import { websiteApi } from '@/api/website'
import { useUserStore } from '~/store/common/user'
import RoundOutlineButton from '~/components/base/button/RoundOutlineButton.vue'
import { storageKey } from '~/utils/storageKey'
import {
  buildHelpFeedbackStorageName,
  resolveHelpFeedbackUserScope,
} from '~/utils/website/helpFeedbackStorage'

const props = defineProps({
  /**
   * @description 内容ID（用于提交反馈统计）
   * @type {string|number}
   */
  contentId: {
    type: [String, Number],
    required: true,
  },
  /**
   * @description “有帮助”统计类型
   * @type {number}
   * @default 4
   */
  yesStatType: {
    type: Number,
    default: 4,
  },
  /**
   * @description “无帮助”统计类型
   * @type {number}
   * @default 5
   */
  noStatType: {
    type: Number,
    default: 5,
  },
  /**
   * @description “有帮助”提示文案
   * @type {string}
   * @default '感谢您的反馈'
   */
  yesMessage: {
    type: String,
    default: '感谢您的反馈',
  },
  /**
   * @description “无帮助”提示文案
   * @type {string}
   * @default '我们会持续改进'
   */
  noMessage: {
    type: String,
    default: '很抱歉，未能帮助到你',
  },
  /**
   * @description 是否使用组件内置接口请求
   * @type {boolean}
   * @default true
   */
  useInternalRequest: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 自定义请求方法（外部注入时使用）
   * @param {Object} payload - 提交参数
   * @param {number} payload.contentId - 内容ID
   * @param {number} payload.statType - 统计类型
   * @param {string} payload.type - 反馈类型 yes/no
   * @returns {Promise<void>}
   */
  onSubmit: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['feedback'])

const userStore = useUserStore()
const feedbackType = ref(null)
const hasSubmitted = ref(false)
const isSubmitting = ref(false)
const pendingType = ref(null)

const lockedType = computed(() => pendingType.value || feedbackType.value)
const feedbackUserScope = computed(() =>
  resolveHelpFeedbackUserScope({
    isLoggedIn: userStore.isLoggedIn,
    userInfo: userStore.userInfo,
  })
)

const getStorageKey = () => {
  const storageName = buildHelpFeedbackStorageName(props.contentId, feedbackUserScope.value)
  return storageName ? storageKey(storageName) : ''
}

const loadFromCache = () => {
  feedbackType.value = null
  hasSubmitted.value = false

  const currentStorageKey = getStorageKey()
  if (import.meta.client && currentStorageKey) {
    try {
      const cached = localStorage.getItem(currentStorageKey)
      if (cached === 'yes' || cached === 'no') {
        feedbackType.value = cached
        hasSubmitted.value = true
      }
    } catch (e) {
      console.warn('读取反馈缓存失败:', e)
    }
  }
}

const saveToCache = (type, currentStorageKey) => {
  if (import.meta.client && currentStorageKey) {
    try {
      localStorage.setItem(currentStorageKey, type)
    } catch (e) {
      console.warn('保存反馈缓存失败:', e)
    }
  }
}

onMounted(loadFromCache)

watch([() => props.contentId, feedbackUserScope], loadFromCache)

/**
 * @description 处理反馈点击
 * @param {string} type - 反馈类型：'yes' 或 'no'
 * @returns {Promise<void>}
 */
const handleClick = async (type) => {
  if (isSubmitting.value) return
  if (hasSubmitted.value) return
  if (!props.contentId) {
    BaseMessage.warning('内容信息不存在')
    return
  }
  const currentStorageKey = getStorageKey()
  if (!currentStorageKey) return
  isSubmitting.value = true
  pendingType.value = type
  try {
    const statType = type === 'yes' ? props.yesStatType : props.noStatType
    if (props.onSubmit) {
      await props.onSubmit({
        contentId: Number(props.contentId),
        statType,
        type,
      })
    } else if (props.useInternalRequest) {
      await websiteApi.updateContentStat({
        contentId: Number(props.contentId),
        statType,
      })
    }
    if (currentStorageKey !== getStorageKey()) return
    feedbackType.value = type
    hasSubmitted.value = true
    saveToCache(type, currentStorageKey)
    emit('feedback', type)
  } catch (error) {
    console.error('提交反馈失败:', error)
    BaseMessage.error('提交反馈失败，请稍后重试')
  } finally {
    isSubmitting.value = false
    pendingType.value = null
  }
}
</script>

<style lang="scss" scoped>
.help-feedback {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-bottom: 0;

  @include respond-to-max('lg') {
    flex-direction: row;
    justify-content: flex-start;
    gap: 16px;
    flex-wrap: nowrap;
  }
}

.help-feedback--submitted {
  @include respond-to-max('lg') {
    flex-wrap: wrap;
    align-items: center;
    row-gap: 8px;
  }
}

.help-feedback__question {
  font-size: 14px;
  font-weight: 400;
  color: $text-title-color;
  margin: 0;

  @include respond-to-max('lg') {
    font-size: 14px;
    text-align: left;
  }
}

.help-feedback__buttons {
  display: flex;
  gap: 16px;

  @include respond-to-max('lg') {
    gap: 12px;
    justify-content: flex-start;
  }
}

.help-feedback__btn--dimmed {
  pointer-events: none;
}

.help-feedback__result {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: $text-title-color;

  @include respond-to-max('md') {
    flex-basis: 100%;
    width: 100%;
    justify-content: flex-start;
    margin-top: 0;
  }
}

.help-feedback__emoji {
  font-size: 20px;
  flex-shrink: 0;
  color: #979797;

  &--colored {
    color: $primary-color;
  }
}

.help-feedback__result-text {
  line-height: 22px;
  min-width: 0;
  white-space: normal;
}
</style>
